// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, ButtonSize, ButtonVariant } from '@usealloy/component-library';
import { useAppDispatch } from '~/store/reduxHooks';
import { IsolatedEditorContainer } from 'flow/editor/styles';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import { THRESHOLD_LIMIT } from 'flow/editor/components/MenuBar/MenuBarModals/ValidateAndSaveVersionModals/validateRegex';
import { ThresholdRightAttributeOrigin } from 'flow/editor/components/NodeEditorDrawer/types';
import ValidityBadge from './ValidityBadge';
import { getEditorOriginType, getRightAttributeValueAsString, selectIsolatedEditorValidator } from './helpers';
import { StyledTextArea, StyledEditorButton } from './styles';
import { ThresholdContext } from '../context';
import { useKeyboardKeyEvent } from 'flow/editor/hooks/useKeyboardKeyEvent';
import { resetSubEditor, createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { isPreviewAction } from 'flow/service/actions';
import { RuleBuilderContext } from '../../../context';
import { UserContext } from '~/dashboard/context';
import { addTrailingSpaces, scrubTrailingSpaces } from '../helpers';
import { confirmShortcut } from 'flow/editor/hooks/useKeyboardKeyEvent/constants';

function IsolatedTextEditor() {
  const dispatch = useAppDispatch();
  const { keyboardFocus, sourceAction } = useContext(RuleBuilderContext);
  const { featureFlags } = useContext(UserContext);
  const { lhsDataType, populatedThreshold } = useContext(ThresholdContext);
  const { publicId, operator } = populatedThreshold;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const originType = getEditorOriginType({ lhsDataType, operator });

  const isRegexEditor = (
    originType === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REGEX
  );
  const isListInclusionEditor = (
    // requires csv style comma delineation format handling
    originType === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_LIST_INCLUSION
  );

  const supportsValidityChecks = isRegexEditor || (
    isListInclusionEditor && (lhsDataType === 'decimal' || lhsDataType === 'integer')
  );

  const stringifiedValue = getRightAttributeValueAsString(populatedThreshold.right.attributeValue);

  const originalTextValue = isListInclusionEditor
    ? addTrailingSpaces(stringifiedValue) // add trailing spaces to comma delineated string
    : stringifiedValue;

  const [pendingTextValue, setPendingTextValue] = useState<string>(originalTextValue);
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => textAreaRef?.current?.focus(), []);

  function closeSubEditor() {
    if (!isValid) return;

    const dispatchedValue = isListInclusionEditor
      ? scrubTrailingSpaces(pendingTextValue)
      : pendingTextValue;

    const stateChange = () => dispatch(
      updateThreshold.rightValue(publicId, dispatchedValue.trim()),
    );

    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo) && !isPreviewAction(sourceAction),
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.threshold, {
          publicId,
          actionId: populatedThreshold.actionId,
        }),
      },
    ));

    dispatch(resetSubEditor());
    keyboardFocus.enable();
  }

  useKeyboardKeyEvent({
    enabled: isValid,
    shortcut: confirmShortcut,
    callback: closeSubEditor,
  });

  const textLength = isListInclusionEditor
    ? scrubTrailingSpaces(pendingTextValue).length
    : pendingTextValue.length;

  const textEditLimitErrors = textLength >= THRESHOLD_LIMIT
    ? `${pendingTextValue.length > THRESHOLD_LIMIT ? 'Exceeds' : 'Reached'} the maximum character limit of ${THRESHOLD_LIMIT} characters.`
    : null;

  // scrub pasted input newline delineators. Not safe for string lists or regex strings
  const scrubNewlines = (text: string) => text.replace(/[\r\n]+/g, '');
  const getTextValue = (text: string) => (
    lhsDataType !== 'string'
      ? scrubNewlines(text)
      : text
  );

  const validator = selectIsolatedEditorValidator(originType, lhsDataType);

  const onInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nativeEvent = e.nativeEvent as InputEvent;
    if (nativeEvent.inputType === 'insertLineBreak') {
      return;
    }

    const text = getTextValue(e.target.value);
    setPendingTextValue(text);

    if (supportsValidityChecks) {
      const isNewValueValid = validator(text);
      if (isNewValueValid !== isValid) setIsValid(isNewValueValid);
    }
  };

  return (
    <IsolatedEditorContainer>
      <StyledTextArea
        isRegexEditor={isRegexEditor}
        ref={textAreaRef}
        value={pendingTextValue}
        error={textEditLimitErrors}
        onInput={onInput}
      />
      <Box
        justifyContent="flex-end"
        gap={2}
        style={{ padding: 'var(--cl-space-1)' }}
      >
        {supportsValidityChecks && (
          <ValidityBadge
            isValid={isValid}
            originType={originType}
            leftDataType={lhsDataType}
          />
        )}
        <StyledEditorButton
          disabled={!isValid}
          variant={ButtonVariant.primary}
          size={ButtonSize.small}
          onClick={() => closeSubEditor()}
        >
          Done
        </StyledEditorButton>
      </Box>
    </IsolatedEditorContainer>
  );
}

export default IsolatedTextEditor;
